/**
 * /observability — operator-facing Sentry status + test-event probe
 *.  Shows whether the SDK is enabled, what release / env it
 * tagged, and lets the operator throw a known exception via the
 * `/v1/admin/sentry/test` endpoint to verify the pipeline end-to-end.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { api, ApiError } from '../lib/api';
import { fmtDateTime, fmtDate } from '../lib/format-time';

interface SentryStatus {
  enabled: boolean;
  release: string;
  environment: string;
  last_event_at: string | null;
  last_event_id: string | null;
  events_captured: number;
}

interface ProbeState {
  last_generated_at: string | null;
  last_observed_at: string | null;
  age_minutes: number | null;
}

interface ArchiveStatus {
  last_export_at: string | null;
  partitions_total: number;
  cold_bytes_total: number;
  recent: Array<{
    table_name: string;
    partition_month: string;
    tenant_id: string | null;
    row_count: number;
    byte_size: number;
    exported_at: string;
  }>;
}

export function AdminObservabilityPage() {
  const [status, setStatus] = useState<SentryStatus | null>(null);
  const [probe, setProbe] = useState<ProbeState | null>(null);
  const [archive, setArchive] = useState<ArchiveStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [testPending, setTestPending] = useState(false);
  const reloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (reloadTimer.current !== null) clearTimeout(reloadTimer.current);
    },
    [],
  );
  const [testResult, setTestResult] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await api<SentryStatus>('/v1/admin/sentry/status');
      setStatus(r);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load Sentry status.');
    }
  }, []);

  // Probe state loaded independently so it doesn't compete with the
  // sentry/status fetch in tests' mock-call ordering, and so a probe-
  // endpoint outage doesn't break the rest of the page.
  const loadProbe = useCallback(async () => {
    try {
      const r = await api<ProbeState>('/v1/admin/observability/status-page-probe');
      setProbe(r);
    } catch {
      setProbe(null);
    }
  }, []);

  // Order matters: load (sentry status) runs first so existing tests
  // that mock-once the responses in order get them in the right slots.
  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void loadProbe();
  }, [loadProbe]);

  // OCP-295: telemetry-archive status. 503 when tiering is disabled
  // (TELEMETRY_ARCHIVE_BUCKET unset) — render nothing in that case.
  useEffect(() => {
    void (async () => {
      try {
        setArchive(await api<ArchiveStatus>('/v1/admin/telemetry/archive-status'));
      } catch {
        setArchive(null);
      }
    })();
  }, []);

  const onTest = useCallback(async () => {
    setTestPending(true);
    setTestResult(null);
    try {
      await api('/v1/admin/sentry/test', { method: 'POST' });
      // Should never reach — the route always throws.
      setTestResult('No exception thrown — pipeline is misconfigured.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 500) {
        // Expected — the route always 500s. The `x-sentry-event`
        // header on the response tells us the event id.
        setTestResult(
          `Sentry test event triggered — backend returned HTTP 500 as expected. Check the Sentry inbox for an event tagged "SentryTestEvent".`,
        );
      } else if (err instanceof ApiError) {
        setTestResult(`Test request failed unexpectedly (HTTP ${err.status}).`);
      } else {
        setTestResult('Test request failed.');
      }
    } finally {
      setTestPending(false);
      // Refresh status to pick up the bumped events_captured counter.
      // OCP-538 - lifecycle-bound: a bare setTimeout would run load()'s
      // setState after unmount/teardown.
      if (reloadTimer.current !== null) clearTimeout(reloadTimer.current);
      reloadTimer.current = setTimeout(() => void load(), 500);
    }
  }, [load]);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Observability</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Sentry pipeline status + test-event probe.  Configure with
          <code className="mx-1 rounded bg-gray-100 dark:bg-gray-800 px-1">SENTRY_DSN</code>
          (runtime) + the build-time <code>SENTRY_AUTH_TOKEN</code>/
          <code>SENTRY_ORG</code>/<code>SENTRY_PROJECT</code> Coolify env-vars
          for source-map uploads.
        </p>
      </div>

      {error && (
        <div role="alert" className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      {!status && !error && <p className="text-sm text-gray-500">Loading…</p>}

      {probe && (
        <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              Status-page freshness probe
            </h2>
            <ProbeBadge ageMinutes={probe.age_minutes} />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Synthetic probe polls{' '}
            <code className="rounded bg-gray-100 dark:bg-gray-800 px-1">status.orboto.io/data/current.json</code>{' '}
            every 5 min. Warns at 30 min staleness, errors at 60 min.
          </p>
          <dl className="mt-3 space-y-2 text-sm">
            <Row
              label="Last observed generated_at"
              value={probe.last_generated_at ?? '—'}
            />
            <Row
              label="Last probe ran"
              value={
                probe.last_observed_at
                  ? `${probe.age_minutes ?? '?'} min ago (${fmtDateTime(probe.last_observed_at)})`
                  : '— no probe runs yet'
              }
            />
          </dl>
        </section>
      )}

      {archive && (
        <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Telemetry archive (cold tiering)
          </h2>
          <p className="mt-1 text-xs text-gray-500">
            Monthly export of telemetry &gt; 90 days old to RustFS.  Verified, then
            range-deleted from Postgres.
          </p>
          <dl className="mt-3 space-y-2 text-sm">
            <Row label="Last export" value={archive.last_export_at ? fmtDateTime(archive.last_export_at) : '— none yet'} />
            <Row label="Partitions archived" value={String(archive.partitions_total)} />
            <Row label="Cold size" value={formatBytes(archive.cold_bytes_total)} />
          </dl>
          {archive.recent.length > 0 && (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="text-gray-500">
                  <tr>
                    <th className="py-1 pr-3">Table</th>
                    <th className="py-1 pr-3">Month</th>
                    <th className="py-1 pr-3">Rows</th>
                    <th className="py-1 pr-3">Size</th>
                    <th className="py-1">Exported</th>
                  </tr>
                </thead>
                <tbody className="text-gray-700 dark:text-gray-300">
                  {archive.recent.slice(0, 10).map((r, i) => (
                    <tr key={i} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="py-1 pr-3 font-mono">{r.table_name.replace('tenant_telemetry_', '').replace('telemetry_', '')}</td>
                      <td className="py-1 pr-3">{r.partition_month}</td>
                      <td className="py-1 pr-3 tabular-nums">{r.row_count}</td>
                      <td className="py-1 pr-3 tabular-nums">{formatBytes(r.byte_size)}</td>
                      <td className="py-1 text-gray-500">{fmtDate(r.exported_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Prometheus scrape</h2>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          The <code className="rounded bg-gray-100 dark:bg-gray-800 px-1">/metrics</code> endpoint serves
          Prometheus-format text protected by a runtime token. Configure the
          scrape via:
        </p>
        <pre
          className="mt-3 overflow-x-auto rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 p-3 text-xs text-gray-700 dark:text-gray-300"
          data-testid="prom-scrape-snippet"
        >{`# prometheus.yml — scrape_config
scrape_configs:
  - job_name: orboto_ocp
    scheme: https
    metrics_path: /metrics
    params:
      token: ['<METRICS_TOKEN>']
    static_configs:
      - targets: ['control.orboto.io:443']
`}</pre>
        <p className="mt-2 text-xs text-gray-500">
          Set <code>METRICS_TOKEN</code> in Coolify env-vars. Rotating
          invalidates the previous token immediately — pick a value at
          least 32 chars (<code>openssl rand -hex 16</code>).
        </p>
      </section>

      {status && (
        <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-5">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Sentry</h2>
            <span
              data-testid="sentry-status-badge"
              className={`rounded px-2 py-0.5 text-xs font-medium ${
                status.enabled
                  ? 'bg-emerald-100 text-emerald-800 dark:text-emerald-300'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              {status.enabled ? 'Connected' : 'Disabled'}
            </span>
          </div>
          <dl className="mt-4 space-y-2 text-sm">
            <Row label="Environment" value={status.environment} />
            <Row label="Release" value={status.release} />
            <Row label="Events captured" value={status.events_captured.toString()} />
            <Row
              label="Last event"
              value={
                status.last_event_at
                  ? `${formatRelative(status.last_event_at)} (id: ${status.last_event_id ?? '—'})`
                  : '— never'
              }
            />
          </dl>
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={onTest}
              disabled={testPending || !status.enabled}
              className="rounded bg-brand-500 px-3 py-2 text-sm font-medium text-white hover:bg-brand-600 disabled:cursor-not-allowed disabled:bg-gray-400"
            >
              {testPending ? 'Triggering…' : 'Send test event'}
            </button>
            {!status.enabled && (
              <p className="text-xs text-gray-500">
                Set <code>SENTRY_DSN</code> in Coolify env to enable.
              </p>
            )}
          </div>
          {testResult && (
            <div
              data-testid="sentry-test-result"
              className="mt-3 rounded border border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800 px-3 py-2 text-xs text-gray-700 dark:text-gray-300"
            >
              {testResult}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function ProbeBadge({ ageMinutes }: { ageMinutes: number | null }) {
  let label: string;
  let cls: string;
  if (ageMinutes === null) {
    label = 'no data';
    cls = 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
  } else if (ageMinutes < 10) {
    label = 'fresh';
    cls = 'bg-emerald-100 text-emerald-800 dark:text-emerald-300';
  } else if (ageMinutes < 30) {
    label = `${ageMinutes} min`;
    cls = 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300';
  } else if (ageMinutes < 60) {
    label = `${ageMinutes} min — warn`;
    cls = 'bg-amber-100 text-amber-800 dark:text-amber-200';
  } else {
    label = `${ageMinutes} min — stale`;
    cls = 'bg-red-100 text-red-800 dark:text-red-300';
  }
  return (
    <span className={`rounded px-2 py-0.5 text-xs font-medium ${cls}`}>{label}</span>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-gray-500">{label}</dt>
      <dd className="text-right font-mono text-xs text-gray-900 dark:text-gray-100">{value}</dd>
    </div>
  );
}

function formatRelative(iso: string): string {
  const now = Date.now();
  const then = new Date(iso).getTime();
  const ageMs = now - then;
  if (ageMs < 60_000) return `${Math.max(0, Math.round(ageMs / 1000))}s ago`;
  if (ageMs < 60 * 60_000) return `${Math.round(ageMs / 60_000)}m ago`;
  if (ageMs < 24 * 60 * 60_000) return `${Math.round(ageMs / (60 * 60_000))}h ago`;
  return `${Math.round(ageMs / (24 * 60 * 60_000))}d ago`;
}

function formatBytes(n: number): string {
  if (!n) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(units.length - 1, Math.floor(Math.log(n) / Math.log(1024)));
  return `${(n / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}
